import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button, Input, Card } from '@/components/ui';
import { useEffectiveBusinessId } from '@/hooks';
import { useCustomers } from '../hooks';
import { CustomerCard } from '../components';
import { PhoneInputList } from '../components';
import { AddressFields } from '@/features/projects/components/AddressFields';
import { deleteCustomer, createCustomerWithPhones } from '../api/customersApi';
import { createDefaultPhoneFormItem } from '../types';
import type { PhoneFormItem } from '../types';

export function CustomersListPage() {
  const effectiveBusinessId = useEffectiveBusinessId();
  const navigate = useNavigate();
  const {
    customers,
    isLoading,
    error,
    refetch,
    search,
    clearSearch,
    isSearching,
  } = useCustomers({
    businessId: effectiveBusinessId,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // New customer form state
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newCustomerForm, setNewCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phones: [createDefaultPhoneFormItem(true)] as PhoneFormItem[],
    billingStreetAddress: '',
    billingUnit: '',
    billingCity: '',
    billingProvince: '',
    billingPostalCode: '',
    billingCountry: 'Canada',
  });

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (value.trim()) {
        search(value);
      } else {
        clearSearch();
      }
    },
    [search, clearSearch]
  );

  const handleDelete = async (customerId: string) => {
    if (!confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    setDeleteError(null);

    try {
      await deleteCustomer(customerId);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete customer';
      setDeleteError(message);
    }
  };

  const handleEdit = (customerId: string) => {
    navigate(`/customers/${customerId}?edit=true`);
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setCreateError(null);
    setNewCustomerForm({
      firstName: '',
      lastName: '',
      email: '',
      phones: [createDefaultPhoneFormItem(true)],
      billingStreetAddress: '',
      billingUnit: '',
      billingCity: '',
      billingProvince: '',
      billingPostalCode: '',
      billingCountry: 'Canada',
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateError(null);
  };

  const handleCreateCustomer = async () => {
    if (!effectiveBusinessId) return;
    if (!newCustomerForm.firstName.trim() || !newCustomerForm.lastName.trim()) {
      setCreateError('First name and last name are required');
      return;
    }

    // Validate billing address fields
    if (
      !newCustomerForm.billingStreetAddress.trim() ||
      !newCustomerForm.billingCity.trim() ||
      !newCustomerForm.billingProvince.trim() ||
      !newCustomerForm.billingPostalCode.trim() ||
      !newCustomerForm.billingCountry.trim()
    ) {
      setCreateError('All billing address fields are required (Street, City, Province, Postal Code, Country)');
      return;
    }

    setIsSubmitting(true);
    setCreateError(null);

    try {
      await createCustomerWithPhones({
        business_id: effectiveBusinessId,
        first_name: newCustomerForm.firstName.trim(),
        last_name: newCustomerForm.lastName.trim(),
        email: newCustomerForm.email.trim() || undefined,
        billing_street_address: newCustomerForm.billingStreetAddress.trim(),
        billing_unit: newCustomerForm.billingUnit.trim() || undefined,
        billing_city: newCustomerForm.billingCity.trim(),
        billing_province: newCustomerForm.billingProvince.trim(),
        billing_postal_code: newCustomerForm.billingPostalCode.trim(),
        billing_country: newCustomerForm.billingCountry.trim(),
        phones: newCustomerForm.phones
          .filter((p) => p.phoneNumber.trim())
          .map((p) => ({
            phone_number: p.phoneNumber,
            phone_type: p.phoneType,
            label: p.label || undefined,
            is_primary: p.isPrimary,
            can_sms: p.canSms,
            notes: p.notes || undefined,
          })),
      });
      setIsCreating(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create customer';
      setCreateError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhonesChange = (phones: PhoneFormItem[]) => {
    setNewCustomerForm((prev) => ({ ...prev, phones }));
  };

  return (
    <DashboardLayout>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Customers</h1>
          <p className="text-sm text-secondary-500 mt-1">
            Manage your customer contacts and phone numbers
          </p>
        </div>
        <Button variant="primary" onClick={handleStartCreate}>
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Customer
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <Input
          name="search"
          placeholder="Search by name, email, or phone..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="max-w-md"
        />
      </div>

      {/* Errors */}
      {(error || deleteError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error || deleteError}</p>
        </div>
      )}

      {/* Create Customer Form */}
      {isCreating && (
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">
              New Customer
            </h2>

            {createError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{createError}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Name Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="firstName"
                  value={newCustomerForm.firstName}
                  onChange={(e) =>
                    setNewCustomerForm((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  placeholder="John"
                  disabled={isSubmitting}
                  required
                />
                <Input
                  label="Last Name"
                  name="lastName"
                  value={newCustomerForm.lastName}
                  onChange={(e) =>
                    setNewCustomerForm((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  placeholder="Smith"
                  disabled={isSubmitting}
                  required
                />
              </div>

              {/* Email */}
              <Input
                label="Email"
                name="email"
                type="email"
                value={newCustomerForm.email}
                onChange={(e) =>
                  setNewCustomerForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder="john@example.com"
                disabled={isSubmitting}
              />

              {/* Phone Numbers */}
              <div className="pt-4 border-t border-secondary-200">
                <h3 className="text-sm font-medium text-secondary-700 mb-3">
                  Phone Numbers
                </h3>
                <PhoneInputList
                  phones={newCustomerForm.phones}
                  onChange={handlePhonesChange}
                  maxPhones={3}
                  disabled={isSubmitting}
                />
              </div>

              {/* Billing Address */}
              <div className="pt-4 border-t border-secondary-200">
                <h3 className="text-sm font-medium text-secondary-700 mb-3">
                  Billing Address
                </h3>
                <AddressFields
                  streetAddress={newCustomerForm.billingStreetAddress}
                  unit={newCustomerForm.billingUnit}
                  city={newCustomerForm.billingCity}
                  province={newCustomerForm.billingProvince}
                  postalCode={newCustomerForm.billingPostalCode}
                  country={newCustomerForm.billingCountry}
                  onChange={(field, value) =>
                    setNewCustomerForm((prev) => ({ ...prev, [field]: value }))
                  }
                  disabled={isSubmitting}
                  fieldPrefix="billing"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancelCreate}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateCustomer}
                  isLoading={isSubmitting}
                >
                  Create Customer
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <svg
              className="animate-spin w-5 h-5 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-secondary-600">Loading customers...</span>
          </div>
        </div>
      ) : customers.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12">
          <svg
            className="w-12 h-12 text-secondary-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {searchTerm ? 'No customers found' : 'No customers yet'}
          </h3>
          <p className="text-secondary-500 mb-6">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Create a project to add your first customer'}
          </p>
          {!searchTerm && (
            <Link to="/projects/create">
              <Button variant="primary">Create Project</Button>
            </Link>
          )}
        </div>
      ) : (
        /* Customer Grid */
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {customers.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onEdit={() => handleEdit(customer.id)}
              onDelete={
                customer.project_count === 0
                  ? () => handleDelete(customer.id)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Searching indicator */}
      {isSearching && (
        <div className="fixed bottom-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-secondary-200">
          <div className="flex items-center gap-2">
            <svg
              className="animate-spin w-4 h-4 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm text-secondary-600">Searching...</span>
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
